import { t } from '@/features/i18n/utils';
import { WorkspaceTypeIcon } from '@/features/workspaces/components/workspace-type-icon';
import { favoritesBySubject, useFavorites } from '@/features/favorites/helpers';
import { ProjectTypeIcon } from '@/features/projects/components/project-type-icon';
import {
  SidebarHeading,
  SidebarSection,
  SidebarSectionPanel,
  SidebarItem,
  SidebarLink,
  SidebarLinkIconButton,
  SidebarList,
  SidebarListEmpty,
  SidebarListEmptyIcon,
} from '@/ui/components/sidebar';
import {
  CatalogueIcon,
  DocumentAttachmentIcon,
  LibrariesIcon,
  MinusSignIcon,
  StarIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import { Icon } from '@/ui/components/icon';
import { HoverScrollText } from '@/ui/components/hover-scroll-text';
import { artifactLinks } from '@/features/artifacts/utils';

export function FavoritesList() {
  const { favorites, toggleFavorite } = useFavorites();
  const favoriteWorkspaces = favoritesBySubject(favorites, 'evaluation');
  const favoriteProjects = favoritesBySubject(favorites, 'project');
  const favoriteBids = favoritesBySubject(favorites, 'bid');
  const favoriteProcurements = favoritesBySubject(favorites, 'procurement');
  const favoriteArtifacts = favoritesBySubject(favorites, 'artifact');

  return (
    <SidebarSection id="favorites">
      <SidebarHeading>{t({ en: 'Favorites', nb: 'Favoritter' })}</SidebarHeading>
      <SidebarSectionPanel>
        {favorites.length === 0 ? (
          <SidebarListEmpty>
            <SidebarListEmptyIcon icon={StarIcon} />
            {t({ en: 'Your favorites will show up here', nb: 'Dine favoritter vil vises her' })}
          </SidebarListEmpty>
        ) : (
          <SidebarList>
            {favoriteBids.map((fav) => (
              <SidebarItem key={fav.id}>
                <SidebarLink
                  to="/project/$projectId/workspaces/$workspaceId/bids/$bidId"
                  params={{
                    projectId: fav.projectId ?? '',
                    workspaceId: fav.evaluationId ?? '',
                    bidId: fav.id,
                  }}
                  data-inactive
                >
                  <Icon icon={CatalogueIcon} />
                  <HoverScrollText>{fav.name}</HoverScrollText>
                  <SidebarLinkIconButton
                    onClick={(e) => {
                      e.preventDefault();
                      toggleFavorite(fav);
                    }}
                  >
                    <Icon icon={MinusSignIcon} />
                  </SidebarLinkIconButton>
                </SidebarLink>
              </SidebarItem>
            ))}
            {favoriteProcurements.map((fav) => (
              <SidebarItem key={fav.id}>
                <SidebarLink
                  to="/project/$projectId/workspaces/$workspaceId/subcontractor-bids/$procurementId"
                  params={{
                    projectId: fav.projectId ?? '',
                    workspaceId: fav.evaluationId ?? '',
                    procurementId: fav.id,
                  }}
                  data-inactive
                >
                  <Icon icon={DocumentAttachmentIcon} />
                  <HoverScrollText>{fav.name}</HoverScrollText>
                  <SidebarLinkIconButton
                    onClick={(e) => {
                      e.preventDefault();
                      toggleFavorite(fav);
                    }}
                  >
                    <Icon icon={MinusSignIcon} />
                  </SidebarLinkIconButton>
                </SidebarLink>
              </SidebarItem>
            ))}
            {favoriteArtifacts.map((fav) => (
              <SidebarItem key={fav.id}>
                <SidebarLink
                  {...artifactLinks({
                    projectId: fav.projectId,
                    workspaceId: fav.evaluationId,
                    artifactId: fav.id,
                  }).detail}
                  data-inactive
                >
                  <Icon icon={LibrariesIcon} />
                  <HoverScrollText>{fav.name}</HoverScrollText>
                  <SidebarLinkIconButton
                    onClick={(e) => {
                      e.preventDefault();
                      toggleFavorite(fav);
                    }}
                  >
                    <Icon icon={MinusSignIcon} />
                  </SidebarLinkIconButton>
                </SidebarLink>
              </SidebarItem>
            ))}
            {favoriteWorkspaces.map((fav) => (
              <SidebarItem key={fav.id}>
                <SidebarLink
                  to="/project/$projectId/workspaces/$workspaceId"
                  params={{ projectId: fav.projectId ?? '', workspaceId: fav.id }}
                  data-inactive
                >
                  <WorkspaceTypeIcon type={fav.type} />
                  <HoverScrollText>{fav.name}</HoverScrollText>
                  <SidebarLinkIconButton
                    onClick={(e) => {
                      e.preventDefault();
                      toggleFavorite(fav);
                    }}
                  >
                    <Icon icon={MinusSignIcon} />
                  </SidebarLinkIconButton>
                </SidebarLink>
              </SidebarItem>
            ))}
            {favoriteProjects.map((fav) => (
              <SidebarItem key={fav.id}>
                <SidebarLink to="/project/$projectId" params={{ projectId: fav.id }} data-inactive>
                  <ProjectTypeIcon type={fav.type} />
                  <HoverScrollText>{fav.name}</HoverScrollText>
                  <SidebarLinkIconButton
                    onClick={(e) => {
                      e.preventDefault();
                      toggleFavorite(fav);
                    }}
                  >
                    <Icon icon={MinusSignIcon} />
                  </SidebarLinkIconButton>
                </SidebarLink>
              </SidebarItem>
            ))}
          </SidebarList>
        )}
      </SidebarSectionPanel>
    </SidebarSection>
  );
}
